import LinearProgress, { LinearProgressProps } from '@mui/material/LinearProgress'
import { useEffect, useState } from 'react'

export type LinearLoaderProps = LinearProgressProps & {
  loading: boolean
  absolute?: boolean
  failed?: boolean
  onClose?: VoidFunction
  autoClose?: boolean
  delay?: number
}

export default function LinearLoader({
  absolute = false,
  autoClose = true,
  delay = 3000,
  loading,
  onClose,
  variant = 'determinate',
  ...others
}: LinearLoaderProps) {
  const [progress, setProgress] = useState(0)

  // Reset progress when not loading
  useEffect(() => {
    if (!loading) setProgress(0)
  }, [loading])

  // Handle the progress increment and auto-close
  useEffect(() => {
    if (!loading || !autoClose || variant === 'indeterminate') return

    const safeDelay = Math.max(delay, 100)
    const increment = 100 / (safeDelay / 100)

    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval)
          /**
           * O setTimeout(..., 0) faz com que a função seja executada após o ciclo atual de renderização,
           * evitando o conflito entre renderizações simultâneas de componentes diferentes.
           * Necessário para evitar o erro de "Cannot update a component while rendering a different component".
           * Ocorre pois o onClose chama um setState que tenta atualizar o estado de um componente
           * que está sendo renderizado no mesmo ciclo.
           */
          setTimeout(() => onClose?.(), 0)
          return 100
        }
        return Math.min(prev + increment, 100)
      })
    }, 100)

    return () => clearInterval(interval)
  }, [loading, autoClose, delay, onClose, variant])

  const getLoaderStyle = (): LinearProgressProps['sx'] => {
    if (!absolute)
      return {
        borderRadius: 4,
        height: 5,
        ...others?.sx,
      }

    return {
      left: 0,
      position: 'absolute',
      top: 0,
      width: '100%',
      ...others?.sx,
    }
  }

  if (!loading) return null

  return (
    <LinearProgress
      {...others}
      variant={variant}
      value={variant === 'determinate' ? progress : undefined}
      sx={getLoaderStyle()}
    />
  )
}
